import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ReactTable from "components/lib/Global/ReactTable";
import SubRowAsync from "./SubRowAsync";
import { format } from "date-fns";
import { deleteCompany, getCompaniesAsync } from "state/slices/companies";
import { getCompanyNameWithSuffix } from "utils/company";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Constants } from "config/constants";
import { handleRecallApplication } from "state/slices/company";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { getCorporateTransactionsAsync } from "state/slices/corporate";
import { recallInitiatedTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { Roles_Permissions_Constants } from "config/roles-permissions";

const TransactionsList = () => {
  const { organization, dashboardRoles, dashboardPermissions } = useSelector(
    (state) => state.organizationsSlice
  );

  const isRegistrar = dashboardRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );

  const isUploader = dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.UPLOADER
  );

  const isReviewer = dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER
  );
  const isApprover = dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.APPROVER
  );

  const { companies } = useSelector((state) => state.companiesSlice);
  const { transactions, getTransactions } = useSelector(
    (state) => state.corporateSlice
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [isRecalling, setIsRecalling] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const allCorporates = [organization];

  const handleEditBtnClick = (original) => {
    if (original.transferYourBusinessInitiator) {
      history.push(`/companies/${original._id}/dashboard?transferYourBusiness`);
    }
  };

  const getCompaniesTransactions = async () => {
    await dispatch(
      getCorporateTransactionsAsync({
        corporateId: organization._id,
        params: {
          limit: 10,
          skip: (currentPage - 1) * 10,
          search: "",
          isCompanyDashboard: true,
        },
      })
    );
  };

  const handleViewBtnClick = (row) => {
    const { original } = row;
    if (
      original.transferYourBusinessInitiator &&
      original.status !== "Active"
    ) {
      history.push(`/transferYourBusiness/${original._id}/view`);
      return;
    }
    if (original.companyName1) {
      history.push(`/companies/${original._id}/view`);
      return;
    }
    if (original?.isForHistory) {
      history.push(
        `/companies/${original?.companyId._id}/dashboard/transactions/${original._id}/review`
      );
    }
    row?.toggleRowExpanded();
  };

  const handleDeleteBtnClick = async (original) => {
    // Implement delete functionality if needed
    if (original.transferYourBusinessInitiator) {
      await dispatch(
        deleteCompany({
          companyId: original._id,
          queryParams: {
            isForTransfer: true,
          },
        })
      );
    }
    await getCompaniesTransactions();
  };

  const showReviewBtn = (original) => {
    return (
      original?.status === Constants.COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW ||
      original?.status === Constants.COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW_II ||
      original?.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW ||
      original?.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW_II ||
      original?.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL
    );
  };

  const handleReviewBtnClick = (original) => {
    if (original?.isForHistory) {
      history.push(
        `/companies/${original?.companyId._id}/dashboard/transactions/${original._id}/review`
      );
    }
    if (original?.transferYourBusinessInitiator) {
      history.push(`/transferYourBusiness/${original._id}/review`);
    }
  };
  const handleRecallBtnClick = async (row) => {
    setSelectedRowIndex(row.index);
    setIsRecalling(true);
    if (row.original?.isForHistory) {
      await dispatch(
        recallInitiatedTransactionChangeAsync({
          companyId: row.original?.companyId._id,
          data: {
            trxId: row.original._id,
            status: row.original?.status,
            isForHistory: true
          },
        })
      );
    } else {
      await dispatch(
        handleRecallApplication({
          companyId: row.original?._id,
        })
      );
    }

    await getCompaniesTransactions();
    setIsRecalling(false);
    setSelectedRowIndex(null);
  };

  const columns = [
    {
      Header: "Batch No.",
      accessor: "BatchNo",
      Cell: ({ row }) => {
        if (row?.original?.registrationName) {
          return row.original.registrationName;
        }
        if (row?.original?.companyId?.registrationName) {
          return row.original.companyId.registrationName;
        }
        if (row?.original?.companyName1) {
          return row?.original?.companyName1;
        }
        return row.original?.names?.companyName || "-";
      },
    },
    {
      Header: "Application Type",
      accessor: "applicationType",
      Cell: ({ row: { original } }) => {
        let type = "Incorporation";
        if (original?.transferYourBusinessInitiator) {
          type = "Transfer Your Business";
        }
        if (original?.isForHistory) {
          type = "History";
        }
        return type;
      },
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row: { original } }) => {
        let type = "N/A";
        if (original?.type) {
          type = original.type;
        }
        if (original?.changeType) {
          type = original.changeType;
        }
        return type;
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };
        if (original?.transferYourBusinessInitiator) {
          return "Transfer Your Business";
        }
        return capitalize(original?.transactionType) || "Incorporation";
      },
    },
    {
      Header: "Tnx No",
      accessor: "tnxNo",
      Cell: ({ row }) => {
        let type = "N/A";
        if (row.original?.trxRef) {
          type = row.original.trxRef;
        }
        if (row?.original?.registrationNumber) {
          type = row?.original?.registrationNumber;
        }
        if (row?.original?.names?.companyRegNumber) {
          return row?.original?.names?.companyRegNumber;
        }
        return type;
      },
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        const { original } = row;
        let status = "N/A";
        if (original?.status) status = original?.status;
        if (row?.original?.applicationType) {
          status = getMaintainYourCompanyReviewStatus(row.original?.status);
        }
        return status === "Active" ? (
          <div className="inline-flex items-center justify-center w-full px-3 py-1 mx-auto text-sm font-medium text-white rounded-full bg-green-primary">
            Registered
          </div>
        ) : (
          status
        );
      },
    },
    {
      Header: "Date",
      Cell: ({ row: { original } }) => {
        return original?.createdAt
          ? format(new Date(original?.createdAt), "yyyy/MM/dd")
          : "N/A";
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        const { original } = row;
        if (isRecalling && selectedRowIndex === row.index) {
          return (
            <span>
              <SpinnerIcon />
              Recalling...
            </span>
          );
        }
        return (
          <TableActionMenu
            row={row}
            hideDeleteBtn={original?.status !== "Draft"}
            hideEditBtn={original?.status !== "Draft"}
            handleEdit={handleEditBtnClick}
            handleDelete={handleDeleteBtnClick}
            showViewBtn
            handleView={handleViewBtnClick}
            showReviewBtn={showReviewBtn(original)}
            handleReview={handleReviewBtnClick}
            handleRecall={handleRecallBtnClick}
            showRecallBtn={showReviewBtn(original)}
          />
        );
      },
    },
  ];

  useEffect(async () => {
    await getCompaniesTransactions();
  }, [organization, currentPage]);

  // Create a function that will render our row sub components
  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <SubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          currentTab="stakeholderTab"
        />
      );
    },
    []
  );

  if (getTransactions.status === "loading") {
    return (
      <div className="items-center bg-white w-full h-full">
        <SpinnerIcon />
      </div>
    );
  }

  return (
    <div className="mt-6 w-full bg-white shadow">
      <ReactTable
        columns={columns}
        data={transactions.items}
        totalRows={transactions.totalCount}
        dataRowCellStyle="pl-4 leading-snug py-2"
        renderRowSubComponent={renderRowSubComponent}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default TransactionsList;
